import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { api } from '../../api/client.js';
import { outbox } from '../../offline/index.js';
import { db } from '../../offline/db.js';

// The engineer's Stock screen. The last copy from the server is kept, so the
// count can be done with no signal; a count sent from here shows at once as
// "Sending…" and is dropped once the server has it.
const MY_KEY = 'nu.stock.my';
const SENT_KEY = 'nu.stock.sent';

const read = (k, fallback) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? fallback; } catch { return fallback; }
};
const write = (k, v) => {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* lasts this visit */ }
};

let sent = read(SENT_KEY, null); // { id, week, at, byName, lines: [{ itemId, description, qty }] }
const listeners = new Set();
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const setSent = (v) => { sent = v; write(SENT_KEY, v); listeners.forEach((fn) => fn()); };

export function useMyStock() {
  const [data, setData] = useState(() => read(MY_KEY, null));
  const [status, setStatus] = useState('idle');
  const [refused, setRefused] = useState(null);
  const local = useSyncExternalStore(subscribe, () => sent);

  const load = useCallback(async () => {
    // The server turned the count down (e.g. an item was missing).
    if (sent?.entryId) {
      const e = await db.outbox.get(sent.entryId).catch(() => null);
      if (e?.status === 'rejected') {
        setRefused(e.message || 'The count wasn’t accepted.');
        await outbox.discard(e.id);
        setSent(null);
      }
    }
    setStatus('loading');
    try {
      const d = await api('/stock/my');
      write(MY_KEY, d);
      setData(d);
      setStatus('ok');
      if (sent && d.count?.id === sent.id) setSent(null);
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load();
    const off = outbox.subscribe(load);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    // While it's on screen, look again every 30 seconds, so an order picked
    // at the storeroom turns green without leaving the page.
    const every = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 30_000);
    return () => { off(); clearInterval(every); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);

  // A count sent from this phone for the week on screen and not back yet.
  const pending = local && data && local.week === data.window.week ? local : null;
  return { data, status, pending, refused, reload: load };
}

export async function sendCount(me, week, items, qty, note) {
  const id = crypto.randomUUID();
  const at = new Date().toISOString();
  const lines = items.map((i) => ({ itemId: i.id, description: i.description, qty: Number(qty[i.id]) }));
  setSent({ id, week, at, byName: me.name, lines, note });
  const entryId = await outbox.enqueue('stock.count', { id, week, at, note, lines: lines.map(({ itemId, qty: q }) => ({ itemId, qty: q })) });
  setSent({ ...sent, entryId });
  return id;
}
